// frontend/src/components/DayShiftOnlyPanel.js
// 日勤のみスタッフの設定パネル（役職者・パート等向け）
//
// チェックされたスタッフは自動シフト割り当てで:
//   - 希望（hopes）は通常通り反映される
//   - 希望のない日は「日勤」のみで埋められる（非日勤パターンには配置されない）
//   - 最大連続勤務日数を超える日は休み (公) になる
//
// 各スタッフに対し以下のモードを選択可能:
//   - 'all'      : 土日祝も日勤のみ（デフォルト）
//   - 'weekdays' : 平日のみ日勤、土日祝は公休

import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabase';
import { useOrganization } from '../contexts/OrganizationContext';

const yearToExperience = (year) => {
  if (!year) return null;
  const now = new Date();
  const currentFiscalYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return currentFiscalYear - Number(year) + 1;
};

const DayShiftOnlyPanel = () => {
  const { currentOrganization } = useOrganization();
  const [allStaff, setAllStaff] = useState([]);
  const [isSettingMode, setIsSettingMode] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);    // 表示モードで全員表示展開
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [searchYear, setSearchYear] = useState('');

  useEffect(() => {
    if (!currentOrganization?.id) return;
    const fetchStaff = async () => {
      setLoading(true);
      try {
        // 通常 select。day_shift_only_mode カラムが未 migrate でも動くよう
        // エラー時は当該カラム抜きで再 fetch する fallback を持つ。
        let { data, error } = await supabase
          .from('staff')
          .select('id, last_name, first_name, employee_id, day_shift_only, day_shift_only_mode, year')
          .eq('organization_id', currentOrganization.id)
          .eq('is_active', true)
          .order('last_name');
        if (error) {
          // カラムが存在しない等の場合、mode 抜きで再試行（後方互換）
          console.warn('day_shift_only_mode 取得失敗、mode 抜きで再試行:', error.message);
          const retry = await supabase
            .from('staff')
            .select('id, last_name, first_name, employee_id, day_shift_only, year')
            .eq('organization_id', currentOrganization.id)
            .eq('is_active', true)
            .order('last_name');
          if (retry.error) throw retry.error;
          data = (retry.data || []).map((s) => ({ ...s, day_shift_only_mode: 'all' }));
        }
        setAllStaff(data || []);
      } catch (e) {
        console.error('スタッフ取得エラー:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchStaff();
  }, [currentOrganization?.id]);

  const dayShiftOnlyStaff = allStaff.filter(s => s.day_shift_only);

  const availableYears = useMemo(() => {
    const years = [...new Set(allStaff.map(s => s.year).filter(Boolean))];
    return years.sort((a, b) => Number(b) - Number(a));
  }, [allStaff]);

  const filteredStaff = useMemo(() => {
    return allStaff.filter(s => {
      const fullName = `${s.last_name || ''}${s.first_name || ''}`;
      const matchName = searchText === '' || fullName.includes(searchText);
      const matchYear = searchYear === '' || String(s.year) === searchYear;
      return matchName && matchYear;
    });
  }, [allStaff, searchText, searchYear]);

  const toggleDayShiftOnly = async (staff) => {
    const newValue = !staff.day_shift_only;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('staff')
        .update({ day_shift_only: newValue })
        .eq('id', staff.id);
      if (error) throw error;
      setAllStaff(prev =>
        prev.map(s => s.id === staff.id ? { ...s, day_shift_only: newValue } : s)
      );
    } catch (e) {
      console.error('日勤のみ設定エラー:', e);
    } finally {
      setSaving(false);
    }
  };

  const setMode = async (staff, mode) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('staff')
        .update({ day_shift_only_mode: mode })
        .eq('id', staff.id);
      if (error) throw error;
      setAllStaff(prev =>
        prev.map(s => s.id === staff.id ? { ...s, day_shift_only_mode: mode } : s)
      );
    } catch (e) {
      console.error('日勤モード変更エラー:', e);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="border rounded-lg p-4 mb-4 bg-white shadow-sm">
        <div className="text-gray-400 text-sm text-center py-2">読み込み中...</div>
      </div>
    );
  }

  // 表示モード: 固定枠（高さ制限+スクロール）or 展開
  const COLLAPSED_MAX_HEIGHT = 130; // 縦サイズの固定高
  return (
    <div className="border rounded-lg p-4 mb-4 bg-white shadow-sm">
      <div className="flex justify-between items-center mb-3 flex-wrap gap-2">
        <span className="text-base font-bold text-gray-800">
          ☀️ 日勤のみスタッフ
          {dayShiftOnlyStaff.length > 0 && (
            <span className="ml-2 text-xs text-yellow-700 font-medium">
              ({dayShiftOnlyStaff.length}名)
            </span>
          )}
        </span>
        <div className="flex gap-2">
          {!isSettingMode && dayShiftOnlyStaff.length > 0 && (
            <button
              onClick={() => setIsExpanded(v => !v)}
              className="px-3 py-1.5 text-sm rounded border font-medium bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200 transition-colors"
            >
              {isExpanded ? '▲ 折りたたむ' : '▼ 全員表示'}
            </button>
          )}
          <button
            onClick={() => { setIsSettingMode(v => !v); setSearchText(''); setSearchYear(''); }}
            className={`px-3 py-1.5 text-sm rounded border font-medium transition-colors ${isSettingMode ? 'bg-gray-200 border-gray-400 text-gray-700' : 'bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200'}`}
          >
            {isSettingMode ? '✕ 閉じる' : '⚙️ 設定'}
          </button>
        </div>
      </div>

      {isSettingMode ? (
        <div>
          <p className="text-xs text-gray-500 mb-3">
            役職者・パートなど、原則として日勤のみ勤務するスタッフをチェックしてください。
            自動割り当てで <strong>希望以外の日は日勤のみ</strong>になり、夜勤・明けなどは付きません（連続勤務日数を超えそうな日は休み）。
          </p>

          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              placeholder="名前で検索..."
              className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-300"
            />
            <select
              value={searchYear}
              onChange={e => setSearchYear(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-300 bg-white"
            >
              <option value="">全学年</option>
              {availableYears.map(y => (
                <option key={y} value={String(y)}>{yearToExperience(y)}年目</option>
              ))}
            </select>
            {(searchText || searchYear) && (
              <button
                onClick={() => { setSearchText(''); setSearchYear(''); }}
                className="px-2 py-1.5 text-sm text-gray-500 hover:text-gray-700 border border-gray-300 rounded-md"
              >
                ✕
              </button>
            )}
          </div>

          <div className="text-xs text-gray-400 mb-2">
            {filteredStaff.length}名表示中（全{allStaff.length}名）
            {dayShiftOnlyStaff.length > 0 && (
              <span className="ml-2 text-yellow-600">日勤のみ: {dayShiftOnlyStaff.length}名</span>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto p-2 bg-gray-50 rounded border">
            {filteredStaff.length === 0 ? (
              <div className="col-span-full text-center text-gray-400 text-sm py-4">該当するスタッフがいません</div>
            ) : (
              filteredStaff.map(s => {
                const mode = s.day_shift_only_mode || 'all';
                return (
                  <div
                    key={s.id}
                    className={`flex flex-col gap-1 p-2 rounded border text-sm transition-colors ${s.day_shift_only ? 'bg-yellow-100 border-yellow-300 text-yellow-800' : 'bg-white border-gray-200 text-gray-700'}`}
                  >
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!s.day_shift_only}
                        onChange={() => toggleDayShiftOnly(s)}
                        disabled={saving}
                        className="accent-yellow-500 flex-shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">{s.last_name} {s.first_name}</div>
                        {s.year && <div className="text-xs text-gray-400">{yearToExperience(s.year)}年目</div>}
                      </div>
                    </label>
                    {s.day_shift_only && (
                      <div className="pl-6 flex gap-1 text-xs">
                        <button
                          onClick={() => setMode(s, 'all')}
                          disabled={saving}
                          className={`px-2 py-0.5 rounded border ${mode === 'all' ? 'bg-yellow-500 border-yellow-600 text-white font-medium' : 'bg-white border-yellow-300 text-yellow-700 hover:bg-yellow-50'}`}
                        >
                          土日祝も日勤
                        </button>
                        <button
                          onClick={() => setMode(s, 'weekdays')}
                          disabled={saving}
                          className={`px-2 py-0.5 rounded border ${mode === 'weekdays' ? 'bg-yellow-500 border-yellow-600 text-white font-medium' : 'bg-white border-yellow-300 text-yellow-700 hover:bg-yellow-50'}`}
                        >
                          平日のみ
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
          {saving && <p className="text-xs text-gray-400 mt-1">保存中...</p>}
        </div>
      ) : (
        <div>
          {dayShiftOnlyStaff.length === 0 ? (
            <div className="text-center text-gray-400 text-sm py-3">日勤のみスタッフは未設定です</div>
          ) : (
            <div
              className="flex flex-wrap gap-2 overflow-y-auto transition-all"
              style={{ maxHeight: isExpanded ? 'none' : COLLAPSED_MAX_HEIGHT }}
            >
              {dayShiftOnlyStaff.map(s => {
                const mode = s.day_shift_only_mode || 'all';
                return (
                  <span
                    key={s.id}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${mode === 'weekdays' ? 'bg-sky-100 text-sky-800 border-sky-300' : 'bg-yellow-100 text-yellow-800 border-yellow-300'}`}
                    title={mode === 'weekdays' ? '平日のみ日勤・土日祝は公休' : '土日祝も含めて日勤のみ'}
                  >
                    {mode === 'weekdays' ? '🗓 平日のみ' : '☀️ 全日'}
                    <span className="ml-1">{s.last_name} {s.first_name}</span>
                    {s.year && <span className="ml-1 opacity-70 text-xs">{yearToExperience(s.year)}年目</span>}
                  </span>
                );
              })}
            </div>
          )}
          {!isExpanded && dayShiftOnlyStaff.length > 0 && (
            <div className="text-xs text-gray-400 mt-2 text-center">
              ↑ スクロールして他のスタッフを見る、もしくは「▼ 全員表示」で展開
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DayShiftOnlyPanel;
